import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'

import { LOGIN_VIEW } from '~/auth/types/login.types'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import ROUTES from '~/shared/constants/routes.const'
import { GET_STARTED_ENTRY_URL } from '~/get-started/entryRoute.const'

import { useCheckIfUserInDbMutation } from '~/auth/apis/auth.api'

import {
  selectFields,
  setBackendErrorActionCreator,
  setFieldsActionCreator,
  setSimpleUserProfileActionCreator,
  setViewActionCreator,
} from '~/auth/slices/login.slice'

import { CurriculumCountryCodes } from '~/curriculum/constants/curriculum.const'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'
import { useGetIsUsaOnboarding } from '~/curriculum/hooks/useGetCurriculumCountryCode'

import { AuthTypes } from '../types/auth.types'

export default function useCheckIfUserInDb(): {
  handleOnCheckIfUserInDb: (event: React.FormEvent) => void
  handleExistingUserCheck: (response: {
    type: AuthTypes
    simpleUserProfile: { avatar: string; nickname: string; userHasPassword: boolean }
  }) => void
  isLoading: boolean
} {
  const dispatch = useAppDispatch()
  const fields = useAppSelector(selectFields)
  const router = useRouter()
  const showUSOnboarding = useGetIsUsaOnboarding()
  const isFamilyInvite = router.pathname === ROUTES.FAMILY_ACCEPT_INVITE
  const [checkIfUserInDb, { data: result, isLoading, isSuccess, isError }] = useCheckIfUserInDbMutation()
  const { data } = useGetUserLocationInfoFromIpQuery()
  const isNorwegian = data?.countryCode === CurriculumCountryCodes.NORWAY.toUpperCase()
  const redirectFrom = (router.query.from || router.query.state) as string
  const hasRedirectFrom = redirectFrom && redirectFrom !== 'undefined'
  const isRedirectingToTOD =
    hasRedirectFrom && (redirectFrom.startsWith('booking') || redirectFrom.startsWith('/no/campaigns/dnb'))

  const handleExistingUserCheck = useCallback(
    async (response: {
      type: AuthTypes
      simpleUserProfile: { avatar: string; nickname: string; userHasPassword: boolean; email?: string }
      // eslint-disable-next-line require-await
    }): Promise<void> => {
      const { type, simpleUserProfile } = response

      dispatch(setFieldsActionCreator(simpleUserProfile.email ? { email: simpleUserProfile.email } : {}))

      if (type === 'email') {
        return void dispatch(setViewActionCreator(LOGIN_VIEW.ENTER_PASSWORD))
      }

      if (['facebook', 'google'].includes(type)) {
        dispatch(
          setSimpleUserProfileActionCreator({
            avatar: simpleUserProfile?.avatar,
            nickname: simpleUserProfile?.nickname,
            userHasPassword: simpleUserProfile?.userHasPassword,
          }),
        )

        switch (type) {
          case AuthTypes.FACEBOOK:
            return void dispatch(setViewActionCreator(LOGIN_VIEW.CONTINUE_WITH_FACEBOOK))

          case AuthTypes.GOOGLE:
            return void dispatch(setViewActionCreator(LOGIN_VIEW.CONTINUE_WITH_GOOGLE))

          default:
            return
        }
      }
    },
    [dispatch],
  )

  useEffect(() => {
    if (isSuccess && result) {
      const { userDoesExist, userHasPassword, type } = result.details

      if (userDoesExist) {
        handleExistingUserCheck({ type, simpleUserProfile: { ...result.details.simpleUserProfile, userHasPassword } })
      }
    }

    if (isError) {
      const isRegisterTarget = isRedirectingToTOD || isFamilyInvite
      router.push({
        pathname: isRegisterTarget ? ROUTES.REGISTER : GET_STARTED_ENTRY_URL.pathname,
        query: {
          ...(isRegisterTarget ? {} : GET_STARTED_ENTRY_URL.query),
          email: fields.email,
          ...router.query,
        },
      })
    }
  }, [
    fields,
    isError,
    isSuccess,
    result,
    router,
    dispatch,
    handleExistingUserCheck,
    isNorwegian,
    isRedirectingToTOD,
    isFamilyInvite,
    showUSOnboarding,
  ])

  function handleOnCheckIfUserInDb(event: React.FormEvent): void {
    event.preventDefault()

    if (!isLoading) {
      dispatch(setBackendErrorActionCreator(''))
      checkIfUserInDb({ email: fields.email })
    }
  }

  return {
    handleOnCheckIfUserInDb,
    isLoading,
    handleExistingUserCheck,
  }
}
